import { AppLayout, Flashbar, FlashbarProps } from '@amzn/awsui-components-core';
import { DateTime } from 'luxon';
import { Location } from 'history';
import React, { type JSX } from 'react';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import FlashbarMessageBroker, { FlashbarMessage } from '../../common/FlashbarMessageBroker';
import RouterPaths from '../../common/RouterPaths';
import { withAppContext } from '../../contexts/AppContext';
import AppContextProps from '../../contexts/AppContextProps';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import { log } from '../../loggers/Logger';
import AdminDashboard from '../administration/Dashboard';
import EditUserPermission from '../administration/EditUserPermission';
import TechWriterDashboardV3 from '../administration/TechWriterDashboardV3';
import UserManagement from '../administration/UserManagement';
import { Category } from '../categories/Category';
import AuthorContentRequestsTable from '../content-requests/AuthorContentRequestsTable';
import ContentRequestCreation from '../content-requests/ContentRequestCreation';
import ContentRequestViewer from '../content-requests/ContentRequestViewer';
import AuthorContent from '../content/AuthorContent';
import AuthorReviews from '../content/AuthorReviews';
import ContentsToPublish from '../content/ContentsToPublish';
import ContentsToReview from '../content/ContentsToReview';
import HomePage from '../content/HomePage';
import PublishPendingComments from '../content/PublishPendingComments';
import AuthorFinalizingViewer from '../publication/AuthorFinalizingViewer';
import FinalizingViewer from '../publication/FinalizingViewer';
import PublicationFeedbackPage from '../publication/PublicationFeedbackPage';
import ReadOnlyPublicationViewer from '../publication/ReadOnlyPublicationViewer';
import BrowserHistory from '../utils/BrowserHistory';
import ErrorBoundary from '../utils/ErrorBoundary';
import AdminContentViewer from '../workflow-viewer/AdminContentViewer';
import AuthorContentViewer from '../workflow-viewer/AuthorContentViewer';
import CreateContentWizard from '../workflow-viewer/content/create-content-wizard/CreateContentWizard';
import CreatePostPage from '../workflow-viewer/post/CreatePostPage';
import ContentHistoryViewer from '../workflow-viewer/ContentHistoryViewer';
import PublisherContentViewer from '../workflow-viewer/PublisherContentViewer';
import { isFeatureEnabled } from '../../common/EnvironmentConfig';
import { getContentPath } from '../../common/ContentPaths';
import ReadOnlyContentViewer from '../workflow-viewer/ReadOnlyContentViewer';
import ReviewerContentViewer from '../workflow-viewer/ReviewerContentViewer';
import TechWriterContentViewer from '../workflow-viewer/TechWriterContentViewer';
import { ContentViewPage } from '../v2-content-view/ContentViewPage';
import ContentEditorPage from '../v2-content-editor/ContentEditorPage';
import ContentNavigator from '../v2-content-navigator/ContentNavigator';
import { BatchUnpublishPage } from '../v2-tools/batch-unpublish/BatchUnpublishPage';
import { withContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { withToolbarActionService } from '../../contexts/ToolbarActionServiceContext';
import { ContentEditorService } from '../../services/ContentEditorService';
import { isInfoPaneTopicId, ToolbarActionService } from '../../services/ToolbarActionService';
import {
  AdminRoute,
  BarRaiserRoute,
  PublisherRoute,
  TechwriterRoute,
  AWSUserRoute,
} from './AuthorizedRoutes';
import AwsDisclaimer from './AwsDisclaimer';
import contentsStyles from './Contents.module.scss';
import Footer from './Footer';
import Header from './Header';
import LoadingPage from './LoadingPage';
import NotFound from './NotFound';
import APGLUserProfileCreatingForm from '../apgl-user-profile/APGLUserProfileCreatingForm';
import APGLUserProfileEditingForm from '../apgl-user-profile/APGLUserProfileEditingForm';
import ContentNotesViewer from '../workflow-viewer/ContentNotesViewer';
import AllContentSuggestionsView from '../content-requests/AllContentSuggestionsView';
import { CreateCollection, EditCollection, MyCollection, CollectionViewer } from '../collection';
import PostViewer from '../workflow-viewer/post/PostViewer';
import WhatsNew from '../workflow-viewer/post/WhatsNew';
import ManagePosts from '../administration/ManagePosts';
import { GeneralBarRaiserDashboard } from '../content/pages/GeneralBarRaiserDashboard';
import RumManager from '../../monitoring/RumManager';
import CommunityCollection from '../collection/CommunityPage/CommunityCollection';
import ContentQualityQuestionnairePage from '../content-quality/ContentQualityQuestionnairePage';
import BarRaiserContentViewer from '../workflow-viewer/BarRaiserContentViewer';
import { AttachmentDownloadPage } from '../v2-download/AttachmentDownloadPage';
import PartnerFlashbarBanner from './PartnerFlashbarBanner';
import LocalStorage from '../../common/LocalStorage';
import EnvironmentConfig, { Stage } from '../../common/EnvironmentConfig';
import { EnabledFeaturesProvider } from '../../contexts/EnabledFeaturesContext';
import { FeatureFlagProvider } from '../../contexts/FeatureFlagContext';
import { UserProvider } from '../../contexts/UserContext';
import { ActionPanel } from '../v2-content-action-panel';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';

// Each help panel topic maps to a renderer function. The param type varies per topic
// (e.g. ContentNode for 'content-type', undefined for most others), so we use unknown here.
type HelpPanelRenderer = (...args: unknown[]) => JSX.Element;

interface ContentsState {
  isLoading: boolean;
  categories: Category[];
  flashbarMessages: FlashbarProps.MessageDefinition[];
  showPersistentBanner: boolean;
  navigationHide: boolean;
  navigationOpen: boolean;
  toolHide: boolean;
  isReading: boolean;
}

const PARTNER_BANNER_ID = 'partner-access-notice-2026';
const AWS_PARTNER_ACCESS_CUTOFF_DATE = '2026-03-15';

// Set ONE_WEEK_MS based on environment
const ONE_WEEK_MS =
  EnvironmentConfig.getEnvironment() === Stage.PROD
    ? 7 * 24 * 60 * 60 * 1000 // 1 week for prod
    : 5 * 60 * 1000; // 5 minutes for testing (local, beta, gamma)

interface ContentsProps extends AppContextProps {
  readonly contentEditorService: ContentEditorService;
  readonly toolbarActionService: ToolbarActionService;
}

class Contents extends React.Component<ContentsProps, ContentsState> {
  private flashbarMessageBroker: FlashbarMessageBroker;
  private unsubscribeFromEditorService: (() => void) | undefined;
  private unsubscribeFromToolbarService: (() => void) | undefined;

  constructor(props: ContentsProps) {
    super(props);
    this.flashbarMessageBroker = FlashbarMessageBroker.create();
    this.flashbarMessageBroker.subscribe(this.receiveFlashbarMessage);

    this.state = {
      categories: [],
      flashbarMessages: [],
      showPersistentBanner: this.shouldShowBanner(),
      isLoading: false,
      navigationHide: true,
      navigationOpen: LocalStorage.getNavigationOpen(),
      toolHide: this.props.toolbarActionService.activePanel === null,
      isReading: false,
    };

    BrowserHistory.listen((location: Location) => {
      this.setState({
        flashbarMessages: [],
      });

      // Automatically clear info pane topic, as when the user navigates away,
      // the current topic might no longer be valid
      this.props.toolbarActionService.clearInfoPaneTopic();

      // If the "info" pane is being displayed, clear that too, so the
      // info pane is collapsed automatically.
      if (this.props.toolbarActionService.activePanel === 'info') {
        this.props.toolbarActionService.activePanel = null;
      }

      log.info({
        location: location.pathname,
        type: 'LOCATION_CHANGED',
        hash: location.hash,
      });
    });
  }

  public async componentDidMount() {
    this.syncNavigationHide();
    this.syncToolHide();
    this.syncIsReading();
    this.unsubscribeFromEditorService = this.props.contentEditorService.on('changed', () => {
      this.syncNavigationHide();
      this.syncIsReading();
    });
    this.unsubscribeFromToolbarService = this.props.toolbarActionService.on(
      'changed',
      this.syncToolHide,
    );
  }

  public async componentWillUnmount() {
    this.flashbarMessageBroker.unsubscribe(this.receiveFlashbarMessage);
    this.unsubscribeFromEditorService?.();
    this.unsubscribeFromToolbarService?.();
  }

  public render() {
    const {
      isLoading,
      isReading,
      flashbarMessages,
      showPersistentBanner,
      navigationHide,
      navigationOpen,
      toolHide,
    } = this.state;
    const { authnInfo } = this.props.appContext;
    if (!authnInfo) {
      throw new Error('authnInfo is not defined. User must be authenticated to render Contents.');
    }
    const { userProfile } = authnInfo;

    if (isLoading) {
      return <LoadingPage />;
    }

    const persistentBannerMessage: FlashbarProps.MessageDefinition[] = showPersistentBanner
      ? [
          {
            content: <PartnerFlashbarBanner />,
            dismissible: true,
            onDismiss: this.handlePersistentBannerDismiss,
          },
        ]
      : [];

    const allFlashbarMessages = [...persistentBannerMessage, ...flashbarMessages];

    const appLayoutContent = (
      <div
        className={isReading ? contentsStyles.reading : undefined}
        // Add some space at the bottom to counter the height of floating footer.
        // Without this, the footer will cover part of the content area
        style={{ paddingBottom: '32px' }}
      >
        <RumManager />
        <Switch>
          <Route exact path="/" component={HomePage} />
          <Route exact path={RouterPaths.CONTENT_REQUESTS} component={AllContentSuggestionsView} />
          <Route exact path={RouterPaths.CREATE_CONTENT} component={CreateContentWizard} />
          <Route exact path="/review-content-request/:requestId" component={ContentRequestViewer} />
          <Route exact path={RouterPaths.MY_CONTENT} component={AuthorContent} />
          <Route exact path={RouterPaths.MY_REQUESTS} component={AuthorContentRequestsTable} />
          <Route exact path={RouterPaths.MY_REVIEWS} component={AuthorReviews} />

          <Route exact path="/content-viewer/author/:contentId" component={AuthorContentViewer} />
          <Route exact path="/content-viewer/:contentId" component={ReadOnlyContentViewer} />
          <Route exact path={RouterPaths.CONTENT_VIEW} render={() => <ContentViewPage />} />
          <Route exact path={RouterPaths.CONTENT_EDIT} render={() => <ContentEditorPage />} />
          <Route
            exact
            path="/content-history-viewer/:contentId/:workflowId"
            component={ContentHistoryViewer}
          />
          <Route
            exact
            path="/content-finalizing-viewer/author/:contentId/:workflowId/:finalizerId"
            component={AuthorFinalizingViewer}
          />

          <Route
            exact
            path={'/publication-feedback/:contentId/:ratingType'}
            component={PublicationFeedbackPage}
          />

          <Redirect from="/content/:contentId/:workflowId" to="/content/:contentId" />
          {/* Legacy publication links land on the v2 viewer once the redesign is on.
              Preserve the incoming hash so deep-links like `/content/:id#feedback-<id>`
              still land on the target on the v2 viewer (see UpdateRequestsTabViewModel). */}
          {isFeatureEnabled('UxRedesign') && (
            <Route
              exact
              path="/content/:contentId"
              render={({ match, location }) => (
                <Redirect
                  to={
                    getContentPath({
                      contentId: match.params.contentId,
                      mode: 'publication',
                    }) + (location.hash ?? '')
                  }
                />
              )}
            />
          )}
          <Route exact path="/content/:contentId" component={ReadOnlyPublicationViewer} />
          <Route
            exact
            path={RouterPaths.CONTENT_REQUEST_CREATION}
            component={ContentRequestCreation}
          />
          <Route
            exact
            path={RouterPaths.REVIEWER_CONTENT_VIEWER}
            component={ReviewerContentViewer}
          />
          <Route exact path={RouterPaths.CONTENT_TO_REVIEW} component={ContentsToReview} />
          <Route exact path={RouterPaths.WHATS_NEW} component={WhatsNew} />

          <Route
            exact
            path={RouterPaths.APGL_USER_PROFILE_REGISTRATION_FORM}
            component={APGLUserProfileCreatingForm}
          />
          <Route
            exact
            path={RouterPaths.APGL_USER_PROFILE_EDITING_FORM}
            component={APGLUserProfileEditingForm}
          />

          <Route exact path={RouterPaths.POST_VIEWER} component={PostViewer} />

          <Route
            exact
            path={RouterPaths.BAR_RAISER_DASHBOARD}
            component={GeneralBarRaiserDashboard}
          />

          <AWSUserRoute exact path={RouterPaths.CREATE_COLLECTION} userProfile={userProfile}>
            <CreateCollection />
          </AWSUserRoute>

          <AWSUserRoute exact path={RouterPaths.EDIT_COLLECTION} userProfile={userProfile}>
            <EditCollection />
          </AWSUserRoute>

          <AWSUserRoute exact path={RouterPaths.MY_COLLECTION} userProfile={userProfile}>
            <MyCollection />
          </AWSUserRoute>

          <AWSUserRoute exact path={RouterPaths.COLLECTION_VIEWER} userProfile={userProfile}>
            <CollectionViewer />
          </AWSUserRoute>

          <AWSUserRoute exact path={RouterPaths.ALL_COLLECTION} userProfile={userProfile}>
            <CommunityCollection />
          </AWSUserRoute>

          <Route
            exact
            path={RouterPaths.BAR_RAISER_CONTENT_VIEWER}
            component={BarRaiserContentViewer}
          />

          <BarRaiserRoute
            exact
            path={RouterPaths.BAR_RAISER_QUESTIONNAIRE}
            userProfile={userProfile}
          >
            <ContentQualityQuestionnairePage />
          </BarRaiserRoute>

          <AdminRoute exact path={RouterPaths.ADMIN_DASHBOARD} userProfile={userProfile}>
            <AdminDashboard />
          </AdminRoute>

          <AdminRoute exact path={RouterPaths.ADMIN_USER_PERMISIONS} userProfile={userProfile}>
            <UserManagement />
          </AdminRoute>

          <AdminRoute exact path={RouterPaths.ADMIN_EDIT_PERMISSIONS} userProfile={userProfile}>
            <EditUserPermission />
          </AdminRoute>

          <AdminRoute exact path={RouterPaths.ADMIN_CONTENT_VIEWER} userProfile={userProfile}>
            <AdminContentViewer />
          </AdminRoute>

          <AdminRoute exact path={RouterPaths.ADMIN_BATCH_UNPUBLISH} userProfile={userProfile}>
            <BatchUnpublishPage />
          </AdminRoute>

          <TechwriterRoute
            exact
            path={RouterPaths.TECH_WRITER_DASHBOARD_NEW}
            userProfile={userProfile}
          >
            <TechWriterDashboardV3 />
          </TechwriterRoute>

          <TechwriterRoute
            exact
            path={RouterPaths.TECH_WRITER_CONTENT_NOTES}
            userProfile={userProfile}
          >
            <ContentNotesViewer />
          </TechwriterRoute>

          <TechwriterRoute
            exact
            path={RouterPaths.TECH_WRITER_CONTENT_VIEWER}
            userProfile={userProfile}
          >
            <TechWriterContentViewer />
          </TechwriterRoute>

          <TechwriterRoute
            exact
            path={RouterPaths.TECH_WRITER_FINALIZING_VIEWR}
            userProfile={userProfile}
          >
            <FinalizingViewer />
          </TechwriterRoute>

          <TechwriterRoute exact path={RouterPaths.CREATE_POST} userProfile={userProfile}>
            <CreatePostPage />
          </TechwriterRoute>

          <TechwriterRoute path={RouterPaths.MANAGE_POSTS} userProfile={userProfile}>
            <ManagePosts />
          </TechwriterRoute>

          <PublisherRoute exact path={RouterPaths.CONTENT_TO_PUBLISH} userProfile={userProfile}>
            <ContentsToPublish />
          </PublisherRoute>

          <PublisherRoute exact path={RouterPaths.MY_PENDING_COMMENTS} userProfile={userProfile}>
            <PublishPendingComments />
          </PublisherRoute>

          <PublisherRoute
            exact
            path={RouterPaths.PUBLISHER_CONTENT_VIEWER}
            userProfile={userProfile}
          >
            <PublisherContentViewer />
          </PublisherRoute>

          <Route exact path={RouterPaths.GET_ATTACHMENTS} component={AttachmentDownloadPage} />

          <Route component={NotFound} />
        </Switch>
      </div>
    );

    const helpPanelCxt = {
      panelHandler: this.toolPanelHandler,
      panelHidden: () => {
        this.props.toolbarActionService.registerActions([]);
      },
      panelClose: () => {
        this.props.toolbarActionService.activePanel = null;
      },
    };

    return (
      // UserProvider lives here (not in AppProviders) because it requires a
      // userProfile prop — AppProviders uses a flat provider array that only
      // supports zero-prop providers.
      <UserProvider userProfile={userProfile}>
        <EnabledFeaturesProvider>
          <FeatureFlagProvider>
            <Router history={BrowserHistory}>
              <div className={'site-container'}>
                <Header auth={authnInfo} />
                <main className="awsui main-content">
                  <ErrorBoundary>
                    <HelpPanelContext.Provider value={helpPanelCxt}>
                      <AppLayout
                        disableContentPaddings={this.disableContentPaddings()}
                        content={appLayoutContent}
                        notifications={
                          <Flashbar className="short-flashbar" items={allFlashbarMessages} />
                        }
                        navigation={<ContentNavigator />}
                        navigationOpen={navigationOpen}
                        navigationHide={navigationHide}
                        onNavigationChange={this.onNavigationChange}
                        toolsHide={toolHide}
                        onToolsChange={() => (this.props.toolbarActionService.activePanel = null)}
                        toolsWidth={290}
                        tools={<ActionPanel />}
                        // If set to Number.MAX_VALUE, the main content panel will occupy the full available width.
                        maxContentWidth={Number.MAX_VALUE}
                        // The tools pane must be in "open" state when tools are
                        // not hidden. This is so CloudScape's visibility toggle
                        // button won't be shown. We render our own "close" button,
                        // which flips the `toolsHide` property above.
                        // Otherwise, it must be in "closed" state to avoid CloudScape
                        // warnings.
                        toolsOpen={!toolHide}
                        headerSelector={'#page-header-container'}
                        footerSelector={'#page-footer'}
                        // footerSelector should only be used when the footer is part of the Content itself.
                        ariaLabels={{
                          navigation: 'Navigation',
                          tools: 'Help panel',
                          toolsClose: 'Close panel',
                        }}
                      />
                    </HelpPanelContext.Provider>
                  </ErrorBoundary>
                </main>
                {!isFeatureEnabled('UxRedesign') && <AwsDisclaimer />}
                <Footer />
              </div>
            </Router>
          </FeatureFlagProvider>
        </EnabledFeaturesProvider>
      </UserProvider>
    );
  }

  private disableContentPaddings = () => {
    const currentPathName = window?.location?.pathname;
    const disablePaddingPaths = [
      '/content-viewer/',
      '/content-finalizing-viewer/',
      '/content/',
      '/content-history-viewer/',
      '/review-content-request/',
      '/user-profile',
      '/collections',
      '/edit/',
      '/view/',
    ];
    return (
      disablePaddingPaths.some((p) => currentPathName.includes(p)) ||
      currentPathName === RouterPaths.HOME_PAGE
    );
  };

  private onNavigationChange = (event: { detail: { open: boolean } }) => {
    const isOpen = event.detail.open;
    LocalStorage.setNavigationOpen(isOpen);
    this.setState({ navigationOpen: isOpen });
  };

  private syncNavigationHide = () => {
    // Drives AppLayout's navigationHide: the left-hand navigation bar is shown when a document
    // is loaded in ContentEditorService, and hidden otherwise.
    const navigationHide = this.props.contentEditorService.loadedDocumentHandle === undefined;
    this.setState((prevState) => {
      // Skip re-render when load state hasn't changed, since 'changed' fires for any service mutation.
      if (prevState.navigationHide === navigationHide) {
        return null;
      }
      return { navigationHide };
    });
  };

  private syncToolHide = () => {
    const toolHide = this.props.toolbarActionService.activePanel === null;
    this.setState((prevState) => {
      if (prevState.toolHide === toolHide) {
        return null;
      }
      return { toolHide };
    });
  };

  private syncIsReading = () => {
    const isReading =
      this.props.contentEditorService.loadedDocumentHandle !== undefined &&
      !this.props.contentEditorService.isEditable;
    this.setState((prevState) => {
      if (prevState.isReading === isReading) {
        return null;
      }
      return { isReading };
    });
  };

  /**
   * Called by legacy code to display the "info" pane on the right hand side.
   * New UIs don't use this method, they interact directly with
   * ToolbarActionService instead.
   */
  private toolPanelHandler = (toolPanelTopic: string) => (toolPanelTopicParam?: ContentNode) => {
    // It is expected that toolPanelTopic passed must be one of the pre-defined topic
    if (!isInfoPaneTopicId(toolPanelTopic)) {
      throw new Error(`Unsupported tool panel topic: ${toolPanelTopic}`);
    }

    // Make sure the toolbar is visible and has an "info" tab.
    this.props.toolbarActionService.registerActions([
      { id: 'info', icon: 'status-info', tooltip: 'Help Info' },
    ]);
    // Change the topic being displayed in the "info" tab
    this.props.toolbarActionService.changeInfoPaneTopic(toolPanelTopic, toolPanelTopicParam);
  };

  private receiveFlashbarMessage = (msg: FlashbarMessage) => {
    msg.onDismiss = () => this.removeFromFlashbarMessages(msg);

    // Build on the pending state: messages pushed back-to-back outside an event handler are
    // batched, so this.state would still lack the earlier ones and overwrite them.
    this.setState(({ flashbarMessages }) => ({
      flashbarMessages: [...flashbarMessages, msg],
    }));

    const { timeout } = msg;
    if (timeout > 0) {
      setTimeout(() => this.removeFromFlashbarMessages(msg), timeout);
    }
  };

  private removeFromFlashbarMessages = (msg: FlashbarMessage) => {
    this.setState(({ flashbarMessages }) => ({
      flashbarMessages: flashbarMessages.filter((m) => m !== msg),
    }));
  };

  private shouldShowBanner = (): boolean => {
    if (DateTime.utc() > DateTime.fromISO(AWS_PARTNER_ACCESS_CUTOFF_DATE)) {
      return false;
    }
    const dismissalTimestamp = LocalStorage.getBannerDismissalTimestamp(PARTNER_BANNER_ID);
    if (!dismissalTimestamp) {
      return true;
    }
    const now = Date.now();
    const timeSinceDismissal = now - dismissalTimestamp;
    return timeSinceDismissal >= ONE_WEEK_MS;
  };

  private handlePersistentBannerDismiss = () => {
    LocalStorage.setBannerDismissalTimestamp(PARTNER_BANNER_ID, Date.now());
    this.setState({ showPersistentBanner: false });
  };
}

export default withAppContext(withToolbarActionService(withContentEditorService(Contents)));
